import React, { useState, useEffect, useCallback } from "react";
import { Container, Tabs, Tab, Button, Modal } from "react-bootstrap";
import PhotoGalleryService from "../../_services/PhotoGalleryService";
import AlbumList from "./components/AlbumList";
import AlbumForm from "./components/AlbumForm";
import AlbumPhotosView from "./components/AlbumPhotosView";
import "./PhotoGalleryPage.css";

interface Album {
  id: number;
  name: string;
  description: string | null;
  coverPhotoUrl: string | null;
  eventDate: string | null;
  sortOrder: number;
  isPublished: number;
  createdBy?: number | null;
  createdDate?: string | null;
  photoCount: number;
}

const PhotoGalleryPage: React.FC = () => {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("albums");
  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Album | null>(null);

  const loadAlbums = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await PhotoGalleryService.getAlbums();
      setAlbums(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load albums", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAlbums();
  }, [loadAlbums]);

  const handleCreateAlbum = () => {
    setEditingAlbum(null);
    setShowAlbumForm(true);
  };

  const handleEditAlbum = (album: Album) => {
    setEditingAlbum(album);
    setShowAlbumForm(true);
  };

  const handleAlbumSaved = () => {
    setShowAlbumForm(false);
    setEditingAlbum(null);
    loadAlbums();
  };

  const handleDeleteAlbum = async () => {
    if (!confirmDelete) return;
    try {
      await PhotoGalleryService.deleteAlbum(confirmDelete.id);
      setConfirmDelete(null);
      loadAlbums();
    } catch (err) {
      console.error("Failed to delete album", err);
    }
  };

  const handleTogglePublish = async (album: Album) => {
    try {
      await PhotoGalleryService.updateAlbum(album.id, {
        isPublished: album.isPublished ? 0 : 1,
      });
      loadAlbums();
    } catch (err) {
      console.error("Failed to toggle publish", err);
    }
  };

  const handleOpenAlbum = (album: Album) => {
    setSelectedAlbum(album);
    setActiveTab("album-photos");
  };

  const handleBackToAlbums = () => {
    setSelectedAlbum(null);
    setActiveTab("albums");
    loadAlbums();
  };

  return (
    <Container fluid className="photo-gallery-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Photo Gallery</h4>
        {activeTab === "albums" && (
          <Button variant="primary" onClick={handleCreateAlbum}>
            + Create Album
          </Button>
        )}
        {activeTab === "album-photos" && (
          <Button variant="secondary" onClick={handleBackToAlbums}>
            Back to Albums
          </Button>
        )}
      </div>

      {activeTab === "albums" && (
        <AlbumList
          albums={albums}
          loading={loading}
          onEdit={handleEditAlbum}
          onDelete={(album) => setConfirmDelete(album)}
          onTogglePublish={handleTogglePublish}
          onOpen={handleOpenAlbum}
        />
      )}

      {activeTab === "album-photos" && selectedAlbum && (
        <AlbumPhotosView
          album={selectedAlbum}
          onAlbumUpdated={loadAlbums}
        />
      )}

      {/* Create/Edit Album Modal */}
      <AlbumForm
        show={showAlbumForm}
        album={editingAlbum}
        onHide={() => {
          setShowAlbumForm(false);
          setEditingAlbum(null);
        }}
        onSaved={handleAlbumSaved}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Album</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the album &quot;{confirmDelete?.name}
          &quot;? This will not delete the photos themselves.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteAlbum}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PhotoGalleryPage;
